import * as yup from 'yup';
import i18n from '../i18n';

// Helper to get localized messages
const getValidationMessages = (t?: any) => {
  const translator = t || ((key: string, opts?: any) => {
    try {
      const translated = opts?.defaultValue || key;
      return translated;
    } catch {
      return opts?.defaultValue || key;
    }
  });

  return {
    required: (field?: string) => translator('validation.required', { defaultValue: 'This field is required', field }),
    email: () => translator('validation.email', { defaultValue: 'Please enter a valid email address' }),
    minLength: (min: number) => translator('validation.minLength', { defaultValue: `Must be at least ${min} characters`, min }),
    maxLength: (max: number) => translator('validation.maxLength', { defaultValue: `Must be less than ${max} characters`, max }),
    phone: () => translator('validation.phone', { defaultValue: 'Please enter a valid phone number' }),
    phoneInvalid: () => translator('validation.phoneInvalid', { defaultValue: 'Invalid phone number format' }),
    password: () => translator('validation.password', { defaultValue: 'Password must be at least 6 characters long' }),
    passwordMatch: () => translator('validation.passwordMismatch', { defaultValue: 'Passwords do not match' }),
    price: () => translator('validation.price', { defaultValue: 'Please enter a valid price' }),
    positive: () => translator('validation.priceInvalid', { defaultValue: 'Must be a positive number' }),
    slug: () => translator('validation.slugInvalid', { defaultValue: 'URL slug contains invalid characters' }),
    url: () => translator('validation.url', { defaultValue: 'Please enter a valid URL' }),
    name: () => translator('validation.name', { defaultValue: 'Name must be at least 2 characters long' }),
    nameInvalid: () => translator('validation.nameInvalid', { defaultValue: 'Name contains invalid characters' }),
  };
};

// Set default Yup locale
yup.setLocale({
  mixed: {
    required: 'This field is required',
    default: 'Invalid value',
  },
  string: {
    email: 'Please enter a valid email address',
    min: 'Must be at least ${min} characters',
    max: 'Must be less than ${max} characters',
  },
  number: {
    min: 'Must be at least ${min}',
    max: 'Must be less than ${max}',
    positive: 'Must be a positive number',
  },
});

// Common validation schemas
export const createValidationSchemas = (t: any = null) => {
  const messages = getValidationMessages(t || ((key: string, opts?: any) => opts?.defaultValue || key));

  return {
    email: yup
      .string()
      .required(messages.required('Email'))
      .email(messages.email()),
    
    password: yup
      .string()
      .required(messages.required('Password'))
      .min(6, messages.password()),
    
    confirmPassword: (passwordField: string = 'password') => yup
      .string()
      .required(messages.required('Confirm Password'))
      .oneOf([yup.ref(passwordField)], messages.passwordMatch()),
    
    name: yup
      .string()
      .required(messages.required('Name'))
      .min(2, messages.minLength(2))
      .max(50, messages.maxLength(50))
      .matches(/^[a-zA-Z\u0600-\u06FF\s]{2,50}$/, messages.nameInvalid()),
    
    firstName: yup
      .string()
      .required(messages.required('First Name'))
      .min(2, messages.minLength(2))
      .max(50, messages.maxLength(50))
      .matches(/^[a-zA-Z\u0600-\u06FF\s]{2,50}$/, messages.nameInvalid()),
    
    lastName: yup
      .string()
      .required(messages.required('Last Name'))
      .min(2, messages.minLength(2))
      .max(50, messages.maxLength(50))
      .matches(/^[a-zA-Z\u0600-\u06FF\s]{2,50}$/, messages.nameInvalid()),
    
    phone: yup
      .string()
      .nullable()
      .transform((value) => (value === '' ? null : value))
      .matches(/^[\+]?[0-9\s\-\(\)]{7,20}$/, messages.phoneInvalid()),
    
    requiredString: (min = 1, max = 1000) => yup
      .string()
      .required(messages.required('Field'))
      .min(min, messages.minLength(min))
      .max(max, messages.maxLength(max)),
    
    title: yup
      .string()
      .required(messages.required('Title'))
      .min(3, messages.minLength(3))
      .max(200, messages.maxLength(200)),
    
    description: yup
      .string()
      .required(messages.required('Description'))
      .min(10, messages.minLength(10))
      .max(1000, messages.maxLength(1000)),
    
    slug: yup
      .string()
      .required(messages.required('Slug'))
      .matches(/^[a-z0-9\-]+$/, messages.slug()),
    
    positive: () => yup
      .number()
      .required(messages.required('Number'))
      .typeError(messages.price())
      .positive(messages.positive())
      .min(0, messages.positive()),
    
    price: yup
      .number()
      .required(messages.required('Price'))
      .typeError(messages.price())
      .positive(messages.positive())
      .min(0, messages.positive()),
    
    url: yup
      .string()
      .nullable()
      .transform((value) => (value === '' ? null : value))
      .url(messages.url()),
    
    subject: yup
      .string()
      .required(messages.required('Subject'))
      .min(3, messages.minLength(3))
      .max(200, messages.maxLength(200)),
    
    message: yup
      .string()
      .required(messages.required('Message'))
      .min(10, messages.minLength(10))
      .max(2000, messages.maxLength(2000)),
  };
};

// Pre-built schemas for common forms
export const authSchemas = (t: any) => {
  const schemas = createValidationSchemas(t);
  return {
    login: yup.object().shape({
      email: schemas.email,
      password: schemas.password,
    }),
    register: yup.object().shape({
      firstName: schemas.firstName,
      lastName: schemas.lastName,
      email: schemas.email,
      phone: schemas.phone,
      password: schemas.password,
      confirmPassword: schemas.confirmPassword('password'),
    }),
  };
};

export const contactSchemas = (t: any, isAuthenticated: boolean = false) => {
  const schemas = createValidationSchemas(t);
  return yup.object().shape({
    name: isAuthenticated 
      ? yup.string().nullable().notRequired()
      : schemas.name,
    email: isAuthenticated
      ? yup.string().nullable().notRequired()
      : schemas.email,
    subject: schemas.subject,
    message: schemas.message,
    priority: yup.string().required().oneOf(['low', 'normal', 'high']),
  });
};

export default createValidationSchemas;

